// ============================================================================
// NON-CONFORMITIES - AUDITOR PAGE
// ============================================================================
// Purpose: Track and manage non-conformity findings
// ============================================================================

import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { auditApi, AuditFinding } from '../../audit/api';
import { useModal } from '../../../components/modal';
import { RefreshCw, AlertTriangle, CheckCircle } from 'lucide-react';
import PageInfoButton from '@/components/PageInfoButton';

const NonConformities: React.FC = () => {
  const { openModal } = useModal();
  const [nonConformities, setNonConformities] = useState<AuditFinding[]>([]);
  const [loading, setLoading] = useState(true);
  const [_selectedNonConformity, _setSelectedNonConformity] = useState<AuditFinding | null>(null);

  useEffect(() => {
    loadNonConformities();
  }, []);

  const loadNonConformities = async () => {
    try {
      const findings = await auditApi.getAuditFindings();
      const nonConformityFindings = findings.filter(finding => finding.finding_type === 'non_conformity');
      setNonConformities(nonConformityFindings);
    } catch (error) {
      console.error('Failed to load non-conformities:', error);
      setNonConformities([]);
    } finally {
      setLoading(false);
    }
  };

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'critical': return 'text-red-700 bg-red-100';
      case 'major': return 'text-orange-700 bg-orange-100';
      case 'minor': return 'text-yellow-700 bg-yellow-100';
      default: return 'text-gray-700 bg-gray-100';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'open': return 'text-red-700 bg-red-100';
      case 'in_progress': return 'text-blue-700 bg-blue-100';
      case 'closed': return 'text-green-700 bg-green-100';
      default: return 'text-gray-700 bg-gray-100';
    }
  };

  const handleUpdateStatus = async (nonConformity: AuditFinding, newStatus: AuditFinding['status']) => {
    try {
      await auditApi.updateAuditFinding(nonConformity.id, { status: newStatus });
      setNonConformities(prev => prev.map(nc =>
        nc.id === nonConformity.id ? { ...nc, status: newStatus } : nc
      ));
      toast.success(`Non-conformity status updated to ${newStatus.replace('_', ' ')}`);
    } catch (error) {
      console.error('Failed to update status:', error);
      toast.error('Failed to update status. Please try again.');
    }
  };

  const handleViewDetails = (nonConformity: AuditFinding) => {
    openModal({
      component: ({ onClose }: { onClose: () => void }) => (
        <div className="p-6 max-w-2xl">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-9 h-9 rounded-lg bg-red-50 flex items-center justify-center">
              <AlertTriangle size={18} className="text-red-600" />
            </div>
            <div>
              <h3 className="text-lg font-bold text-slate-900">Non-Conformity Details</h3>
              <p className="text-slate-500 text-sm">Review finding information</p>
            </div>
          </div>

          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-500 mb-1">Title</label>
                <p className="text-sm font-semibold text-slate-900">{nonConformity.title}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-500 mb-1">Severity</label>
                <span className={`inline-flex px-2 py-1 rounded-md font-bold text-[10px] ${getSeverityColor(nonConformity.severity)}`}>
                  {nonConformity.severity}
                </span>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-500 mb-1">Description</label>
              <p className="text-sm text-slate-700">{nonConformity.description}</p>
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-500 mb-1">Status</label>
                <span className={`inline-flex px-2 py-1 rounded-md font-bold text-[10px] ${getStatusColor(nonConformity.status)}`}>
                  {nonConformity.status}
                </span>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-500 mb-1">Company ID</label>
                <p className="text-sm text-slate-900">{nonConformity.company_id}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-500 mb-1">Created</label>
                <p className="text-sm text-slate-900">{new Date(nonConformity.created_at).toLocaleDateString()}</p>
              </div>
            </div>

            <div className="flex justify-end pt-4">
              <button
                onClick={onClose}
                className="px-4 py-2.5 bg-slate-600 text-white font-medium text-sm rounded-lg hover:bg-slate-700 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      ),
      options: {
        size: 'large'
      }
    });
  };

  const handleFollowUp = (nonConformity: AuditFinding) => {
    toast.info(`Follow-up initiated for: ${nonConformity.title}`);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-slate-50">
        <div className="flex flex-col items-center gap-4">
          <RefreshCw className="text-blue-600 animate-spin" size={40} />
          <p className="text-slate-600 font-medium text-sm">Loading non-conformities...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-6xl mx-auto px-6 py-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <div className="flex items-center gap-1"><h1 className="text-3xl font-bold text-slate-900">Non-Conformities</h1><PageInfoButton /></div>
            <p className="text-slate-500 text-sm">Track and manage non-conformity findings requiring corrective action</p>
          </div>
          <button
            onClick={loadNonConformities}
            className="flex items-center gap-2 px-4 py-2.5 bg-white border border-slate-200 rounded-lg text-sm font-medium text-slate-700 hover:bg-slate-50 transition-colors"
          >
            <RefreshCw size={16} />
            Refresh
          </button>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-3xl border border-slate-100 p-4" style={{ boxShadow: "0 4px 20px rgba(0,0,0,0.02)" }}>
            <p className="text-xs font-medium text-slate-500 mb-1">Total</p>
            <p className="text-3xl font-bold text-slate-900">{nonConformities.length}</p>
          </div>

          <div className="bg-white rounded-3xl border border-slate-100 p-4" style={{ boxShadow: "0 4px 20px rgba(0,0,0,0.02)" }}>
            <p className="text-xs font-medium text-slate-500 mb-1">Open</p>
            <p className="text-2xl font-bold text-red-600">
              {nonConformities.filter(nc => nc.status === 'open').length}
            </p>
          </div>

          <div className="bg-white rounded-3xl border border-slate-100 p-4" style={{ boxShadow: "0 4px 20px rgba(0,0,0,0.02)" }}>
            <p className="text-xs font-medium text-slate-500 mb-1">In Progress</p>
            <p className="text-2xl font-bold text-blue-600">
              {nonConformities.filter(nc => nc.status === 'in_progress').length}
            </p>
          </div>

          <div className="bg-white rounded-3xl border border-slate-100 p-4" style={{ boxShadow: "0 4px 20px rgba(0,0,0,0.02)" }}>
            <p className="text-xs font-medium text-slate-500 mb-1">Closed</p>
            <p className="text-2xl font-bold text-green-600">
              {nonConformities.filter(nc => nc.status === 'closed').length}
            </p>
          </div>
        </div>

        {/* Non-Conformities List */}
        <div className="space-y-4 mb-6">
          {nonConformities.map((nc) => (
            <div key={nc.id} className="bg-white rounded-3xl border border-slate-100 p-5" style={{ boxShadow: "0 4px 20px rgba(0,0,0,0.02)" }}>
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-3">
                  <AlertTriangle size={18} className={
                    nc.severity === 'critical' ? 'text-red-600' :
                    nc.severity === 'major' ? 'text-orange-600' :
                    'text-yellow-600'
                  } />
                  <div>
                    <h3 className="text-sm font-semibold text-slate-900">{nc.title}</h3>
                    <p className="text-xs text-slate-500">Compliance Page #{nc.compliance_page_id}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <span className={`px-2 py-1 rounded-md font-bold text-[10px] capitalize ${getStatusColor(nc.status)}`}>
                    {nc.status.replace('_', ' ')}
                  </span>
                  <span className={`px-2 py-1 rounded-md font-bold text-[10px] capitalize ${getSeverityColor(nc.severity)}`}>
                    {nc.severity}
                  </span>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-3">
                <div>
                  <p className="text-xs text-slate-500">Company ID</p>
                  <p className="text-sm font-medium text-slate-900">{nc.company_id}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500">Auditor ID</p>
                  <p className="text-sm font-medium text-slate-900">{nc.auditor_id}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500">Target Completion</p>
                  <p className="text-sm font-medium text-slate-900">{nc.target_completion_date ? new Date(nc.target_completion_date).toLocaleDateString() : 'Not set'}</p>
                </div>
              </div>

              <div className="mb-3">
                <p className="text-xs text-slate-500 mb-1">Description</p>
                <p className="text-sm text-slate-700">{nc.description}</p>
              </div>

              {nc.evidence_reference && (
                <div className="mb-3">
                  <p className="text-xs text-slate-500 mb-1">Evidence Reference</p>
                  <p className="text-sm text-slate-700">{nc.evidence_reference}</p>
                </div>
              )}

              {nc.corrective_action && (
                <div className="mb-3">
                  <p className="text-xs text-slate-500 mb-1">Corrective Action Required</p>
                  <p className="text-sm text-slate-700">{nc.corrective_action}</p>
                </div>
              )}

              <div className="flex items-center gap-3">
                <button
                  onClick={() => {
                    if (nc.status === 'closed') {
                      toast.info('This non-conformity is already closed');
                      return;
                    }
                    const newStatus = nc.status === 'open' ? 'in_progress' : 'closed';
                    handleUpdateStatus(nc, newStatus);
                  }}
                  className="px-4 py-2.5 bg-blue-600 text-white font-medium text-sm rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  disabled={nc.status === 'closed'}
                >
                  Update Status
                </button>
                <button
                  onClick={() => handleViewDetails(nc)}
                  className="px-4 py-2.5 border border-slate-200 text-slate-700 font-medium text-sm rounded-lg hover:bg-slate-50 transition-colors"
                >
                  View Details
                </button>
                <button
                  onClick={() => handleFollowUp(nc)}
                  className="px-4 py-2.5 border border-slate-200 text-slate-700 font-medium text-sm rounded-lg hover:bg-slate-50 transition-colors"
                >
                  Follow Up
                </button>
              </div>
            </div>
          ))}
        </div>

        {nonConformities.length === 0 && (
          <div className="bg-white rounded-3xl border border-slate-100 p-12">
            <div className="w-10 h-10 bg-green-50 rounded-lg flex items-center justify-center mx-auto mb-3">
              <CheckCircle size={20} className="text-green-600" />
            </div>
            <h3 className="text-sm font-semibold text-slate-900 mb-1">No Non-Conformities</h3>
            <p className="text-sm text-slate-500">All compliance checks have passed successfully.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default NonConformities;
